import { forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { useButtonPressAnimation } from '../../animations/useButtonPressAnimation';

const VARIANTS = {
  primary: 'bg-accent text-background hover:bg-accent-hover shadow-card hover:shadow-glow-sm',
  // hover:bg-[#28324A] is a manual one-step lighten off background.elevated
  // (#1A2333) — no lighter token exists in the design system to reach for,
  // and this is the one place a subtle "background lightens on hover"
  // micro-interaction was asked for (secondary CTAs like the hero's Log In).
  secondary: 'bg-background-elevated text-foreground border border-border hover:border-border-strong hover:bg-[#28324A]',
  ghost: 'bg-transparent text-foreground hover:bg-background-elevated',
  danger: 'bg-danger text-white hover:bg-danger/90',
};

const SIZES = {
  sm: 'text-sm px-3 py-1.5 rounded-lg',
  md: 'text-sm px-4 py-2.5 rounded-xl',
  lg: 'text-base px-6 py-3 rounded-xl',
};

const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', className, children, disabled, isLoading, ...props },
  forwardedRef
) {
  const localRef = useRef(null);
  useButtonPressAnimation(localRef);

  return (
    <button
      ref={(node) => {
        localRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      disabled={disabled || isLoading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors duration-150',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {isLoading && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
});

export default Button;
